import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtTRY } from "../../lib/format";

export default function ProductsPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const load = () => {
    api.get(`/admin/products?q=${encodeURIComponent(q)}&page=${page}&limit=30`)
      .then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [q, page]);

  const remove = async (p) => {
    if (!window.confirm(`"${p.name}" silinsin mi? Varyantları da silinir.`)) return;
    try {
      await api.delete(`/admin/products/${p.product_id}`);
      toast.success("Ürün silindi");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleActive = async (p) => {
    try {
      const { data: full } = await api.get(`/admin/products/${p.product_id}`);
      const { variants, ...payload } = full;
      await api.put(`/admin/products/${p.product_id}`, { ...payload, active: !p.active });
      toast.success(p.active ? "Ürün pasife alındı" : "Ürün aktifleştirildi");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-products">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display font-extrabold text-2xl text-white">Ürünler <span className="text-sm text-neutral-500 font-normal">({data?.total ?? 0})</span></h1>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Ad, SKU, ürün kodu..."
              className="h-10 pl-9 pr-4 bg-[#181B20] border border-[#272B33] rounded-xl text-sm text-white w-64 focus:outline-none focus:border-blue-500"
              data-testid="admin-product-search" />
          </div>
          <Link to="/admin/urunler/yeni" className="h-10 px-4 rounded-xl bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-blue-500" data-testid="admin-product-new">
            <Plus size={16} /> Yeni Ürün
          </Link>
        </div>
      </div>

      <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]" data-testid="admin-products-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 border-b border-[#272B33]">
              <th className="px-5 py-3.5">Ürün</th>
              <th className="px-4 py-3.5">SKU</th>
              <th className="px-4 py-3.5">Marka</th>
              <th className="px-4 py-3.5">Fiyat</th>
              <th className="px-4 py-3.5">Stok</th>
              <th className="px-4 py-3.5">Durum</th>
              <th className="px-4 py-3.5 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {data?.items?.map((p) => (
              <tr key={p.product_id} className="border-b border-[#272B33]/50 hover:bg-[#1E222A]/50" data-testid={`admin-product-row-${p.sku || p.product_id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {p.images?.[0] && <img src={p.images[0]} alt="" className="w-10 h-12 rounded-lg object-cover bg-[#0F1115]" />}
                    <div>
                      <p className="font-medium text-white line-clamp-1">{p.name}</p>
                      <p className="text-[11px] text-neutral-500">
                        {p.has_variants ? `${p.variants?.length || 0} varyant` : "Varyantsız"}
                        {p.is_demo && <span className="ml-2 text-amber-500 font-mono-ed text-[9px] uppercase">demo</span>}
                        {p.tags?.includes("unisex-inceleme") && (
                          <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 text-[10px] font-semibold" data-testid="admin-review-badge">Unisex / İnceleme</span>
                        )}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono-ed text-xs text-neutral-400">{p.sku}</td>
                <td className="px-4 py-3 text-neutral-400">{p.brand_name || "—"}</td>
                <td className="px-4 py-3">
                  <span className="text-white font-semibold">{fmtTRY(p.sale_price ?? p.price)}</span>
                  {p.sale_price && <span className="block text-[11px] text-neutral-500 line-through">{fmtTRY(p.price)}</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={`font-bold ${p.total_stock <= 0 ? "text-rose-400" : p.total_stock <= (p.critical_stock || 5) ? "text-amber-400" : "text-emerald-400"}`} data-testid="admin-product-stock">
                    {p.total_stock}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActive(p)}
                    className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${p.active ? "bg-emerald-500/15 text-emerald-400" : "bg-neutral-500/15 text-neutral-400"}`}
                    data-testid="admin-product-toggle">
                    {p.active ? "Aktif" : "Pasif"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Link to={`/admin/urunler/${p.product_id}`} className="p-2 rounded-lg text-neutral-400 hover:text-blue-400 hover:bg-blue-500/10" data-testid="admin-product-edit"><Pencil size={15} /></Link>
                    <button onClick={() => remove(p)} className="p-2 rounded-lg text-neutral-400 hover:text-rose-400 hover:bg-rose-500/10" data-testid="admin-product-delete"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.items?.length === 0 && <p className="text-center text-neutral-500 text-sm py-12">Ürün bulunamadı</p>}
      </div>

      {data?.pages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: data.pages }).map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              className={`w-9 h-9 rounded-lg text-sm font-semibold ${page === i + 1 ? "bg-blue-600 text-white" : "bg-[#181B20] border border-[#272B33] text-neutral-400"}`}>
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
